import React, { useState } from "react";
import { Flex, Text, Textarea } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";

// 취소 사유 최대 길이 — 배너 한 줄에 담기는 분량으로 제한한다.
const MAX_REASON_LENGTH = 200;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  // 사유는 선택 입력이라 비어 있으면 undefined로 올라간다.
  onConfirm: (cancelReason?: string) => void;
  // 취소 처리 중 — 버튼을 잠가 이중 취소를 막는다.
  isCanceling?: boolean;
}

// 주문 취소 확인 모달 — 되돌릴 수 없는 액션이라 주문번호를 문구에 박아 대상을 못 박는다.
// 실제 mutation은 호출부(헤더)가 소유한다.
function PathPickOrderCancelModal({
  isOpen,
  onClose,
  orderNumber,
  onConfirm,
  isCanceling = false,
}: Props) {
  const { t } = useI18nHelper();
  const [cancelReason, setCancelReason] = useState("");

  // 닫을 때 입력값을 비워야 다시 열었을 때 지난 사유가 남지 않는다.
  const handleClose = () => {
    setCancelReason("");
    onClose();
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={handleClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면
      // 스크롤로도 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(460px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={handleClose}
        title={t(`${ORDER_DETAIL_KEY}cancel_confirm_title`)}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <Text textStyle={"body-sm-medium"} color={"#444440"}>
            {t(`${ORDER_DETAIL_KEY}cancel_confirm_description`, {
              orderNumber,
            })}
          </Text>

          <Flex flexDirection={"column"} gap={"4px"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t(`${ORDER_DETAIL_KEY}cancel_confirm_reason_label`)}
            </Text>
            <Textarea
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder={t(
                `${ORDER_DETAIL_KEY}cancel_confirm_reason_placeholder`
              )}
              maxLength={MAX_REASON_LENGTH}
              minHeight={"96px"}
              fontSize={"13px"}
              borderRadius={"4px"}
              boxShadow={"0 0 0 0.5px #8F8F8C"}
              padding={"8px 12px"}
              _placeholder={{ color: "#8F8F8C" }}
            />
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("common:cancel")}
        confirmName={t(`${ORDER_DETAIL_KEY}cancel_confirm_submit`)}
        onCancel={handleClose}
        onConfirm={() => onConfirm(cancelReason.trim() || undefined)}
        isLoading={isCanceling}
        isConfirmDisabled={isCanceling}
        // 되돌릴 수 없는 액션이라 기본 파랑 대신 danger 색을 쓴다.
        confirmBtnBgColor={"#CC1D1D"}
        // "주문 취소 확정"이 96px 고정폭에 들어가지 않는다.
        confirmBtnProps={{ width: "auto", _hover: { background: "#A81717" } }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickOrderCancelModal;
